import { createBrowserRouter, RouterProvider, Outlet } from "react-router"
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar"
import { AppSidebar } from "@/components/layout/AppSidebar"
import { Breadcrumbs } from "@/components/layout/Breadcrumbs"
import { ProtectedRoute } from "@/components/ProtectedRoute"
import { LoginPage } from "@/pages/Login"
import { Dashboard } from "@/pages/Dashboard"
import { RunsList } from "@/pages/RunsList"
import { RunDetail } from "@/pages/RunDetail"
import { Phenotypes } from "@/pages/Phenotypes"
import { PhenotypeDetail } from "@/pages/PhenotypeDetail"
import { Upload } from "@/pages/Upload"
import { Settings } from "@/pages/Settings"
import { DiffViewerPage } from "@/pages/DiffViewerPage"
import type { RouteHandle } from "@/types/react-router"

/**
 * Main Layout component with sidebar and breadcrumbs.
 * Wraps all authenticated routes.
 */
function Layout() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        {/* Mobile header with hamburger menu */}
        <header className="flex h-14 items-center gap-4 border-b bg-background px-4 md:hidden">
          <SidebarTrigger />
          <h1 className="text-lg font-semibold">IMC Graphs</h1>
        </header>

        {/* Main content */}
        <div className="flex-1 min-w-0 overflow-y-auto p-4 md:p-6 lg:p-8">
          <Breadcrumbs />
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

/**
 * Router configuration with all application routes.
 * Routes define breadcrumb metadata via handle.
 */
const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Dashboard />,
        handle: {
          breadcrumb: () => "Dashboard",
        } satisfies RouteHandle,
      },
      {
        path: "runs",
        element: <RunsList />,
        handle: {
          breadcrumb: () => "Runs",
        } satisfies RouteHandle,
      },
      {
        path: "runs/:fileId",
        element: <RunDetail />,
        handle: {
          breadcrumb: (match) => `Run #${match.params.fileId?.slice(0, 8)}...`,
        } satisfies RouteHandle,
      },
      {
        path: "phenotypes",
        element: <Phenotypes />,
        handle: {
          breadcrumb: () => "Phenotypes",
        } satisfies RouteHandle,
      },
      {
        path: "phenotypes/:id",
        element: <PhenotypeDetail />,
        handle: {
          breadcrumb: (match) => `Phenotype ${match.params.id?.slice(0, 8)}...`,
        } satisfies RouteHandle,
      },
      {
        path: "upload",
        element: <Upload />,
        handle: {
          breadcrumb: () => "Upload",
        } satisfies RouteHandle,
      },
      {
        path: "settings",
        element: <Settings />,
        handle: {
          breadcrumb: () => "Settings",
        } satisfies RouteHandle,
      },
      {
        path: "diff",
        element: <DiffViewerPage />,
        handle: {
          breadcrumb: () => "Diff Viewer",
        } satisfies RouteHandle,
      },
    ],
  },
])

export function App() {
  return <RouterProvider router={router} />
}
